import { Injectable, signal } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { MUTATION_ADD_TO_CART, QUERY_CART } from '../queries/cart';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  items = signal<any[]>([]);
  cartTotal = signal<string>('');
  isUpdating = signal<boolean>(false);

  constructor(private apollo: Apollo) {
    this.fetchCart();
  }

  fetchCart() {
    this.isUpdating.set(true);
    this.apollo.query({
      query: QUERY_CART,
      fetchPolicy: 'network-only' // always fetch to sync session
    }).subscribe({
      next: ({ data }: any) => {
        this.updateState(data?.cart);
        this.isUpdating.set(false);
      },
      error: () => this.isUpdating.set(false)
    });
  }
  
  addToCart(product: any, quantity: number = 1) {
    const productId = product.databaseId || product.id; 
    const parsedId = parseInt(productId, 10);
    
    // If the product is already in the cart, increment its quantity instead
    const existingItem = this.items().find(item => item.product?.node?.databaseId === parsedId);
    if (existingItem) {
      this.updateQuantity(existingItem.key, existingItem.quantity + quantity);
      return;
    }

    this.isUpdating.set(true);
    this.apollo.mutate({
      mutation: MUTATION_ADD_TO_CART,
      variables: {
        input: {
          productId: parsedId,
          quantity
        }
      }
    }).subscribe({
      next: ({ data }: any) => {
        this.updateState(data?.addToCart?.cart);
        this.isUpdating.set(false);
      },
      error: (error) => {
        console.error('Error adding to cart', error);
        this.isUpdating.set(false);
      }
    });
  }

  private updateState(cart: any) {
    if (!cart) return;
    this.items.set(cart.contents?.nodes || []);
    this.cartTotal.set(cart.total || 'S/ 0.00');
  }

  updateQuantity(key: string, quantity: number) {
    if (quantity < 1) return; // Use removeItem instead
    this.isUpdating.set(true);
    import('../queries/cart').then(({ MUTATION_UPDATE_QUANTITY }) => {
      this.apollo.mutate({
        mutation: MUTATION_UPDATE_QUANTITY,
        variables: { input: { items: [{ key, quantity }] } }
      }).subscribe({
        next: ({ data }: any) => {
          this.updateState(data?.updateItemQuantities?.cart);
          this.isUpdating.set(false);
        },
        error: (error) => {
          console.error('Error updating quantity', error);
          this.isUpdating.set(false);
        }
      });
    });
  }

  removeItem(key: string) {
    this.isUpdating.set(true);
    import('../queries/cart').then(({ MUTATION_REMOVE_ITEM }) => {
      this.apollo.mutate({
        mutation: MUTATION_REMOVE_ITEM,
        variables: { input: { keys: [key] } }
      }).subscribe({
        next: ({ data }: any) => {
          this.updateState(data?.removeItemsFromCart?.cart);
          this.isUpdating.set(false);
        },
        error: (error) => {
          console.error('Error removing item', error);
          this.isUpdating.set(false);
        }
      });
    });
  }

  submitCheckout(formData: any): Observable<any> {
    this.isUpdating.set(true);
    // Return observable so the component can handle routing/success
    return new Observable(observer => {
      import('../queries/cart').then(({ MUTATION_CHECKOUT }) => {
        this.apollo.mutate({
          mutation: MUTATION_CHECKOUT,
          variables: {
            input: {
              paymentMethod: "cod", 
              billing: {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                address1: formData.address1,
                address2: formData.address2,
                city: formData.city
              }
            }
          }
        }).subscribe({
          next: ({ data }: any) => {
            this.items.set([]);
            this.cartTotal.set('S/ 0.00');
            this.isUpdating.set(false);
            observer.next(data?.checkout?.order?.orderNumber);
            observer.complete();
          },
          error: (error) => {
            console.error('Error in checkout', error);
            this.isUpdating.set(false);
            observer.error(error);
          }
        });
      });
    });
  }
}
